import classNames from "classnames";
import { CSSProperties } from "react";
import {
	GridContainerProps,
	GridItemProps,
	GridListItemContainerProps,
	GridTextItemProps,
	MediaProps,
	ResponsiveImagesMap,
} from "../../../constants/pageSectionTypes";
import Picture from "../../ui/Picture/Picture";
import MarkdownRenderer from "../MarkdownRenderer/MarkdownRenderer";
import "./TitledListWithMedia.scss";

export type TitledListWithMediaProps = {
	title: GridTextItemProps;
	list: GridListItemContainerProps<GridTextItemProps>;
	listType?: "ul" | "ol";
	media?: GridListItemContainerProps<MediaProps>;
	grid?: Partial<GridContainerProps>;
};

const generateGridTemplateStyles = (
	gridItem?: Partial<
		Pick<GridContainerProps, "gridTemplateColumns" | "gridTemplateRows">
	>,
	gridElementLabel: string = "item",
): CSSProperties => {
	return {
		...(gridItem?.gridTemplateColumns
			? {
					[`--grid-${gridElementLabel}-template-columns`]: Array.isArray(
						gridItem.gridTemplateColumns,
					)
						? gridItem.gridTemplateColumns[0]
						: gridItem?.gridTemplateColumns,
				}
			: {}),
		...(gridItem?.gridTemplateRows
			? {
					[`--grid-${gridElementLabel}-template-rows`]: Array.isArray(
						gridItem.gridTemplateRows,
					)
						? gridItem.gridTemplateRows[0]
						: gridItem?.gridTemplateRows,
				}
			: {}),
		...(Array.isArray(gridItem?.gridTemplateColumns) &&
		gridItem?.gridTemplateColumns.length > 1
			? {
					[`--grid-${gridElementLabel}-template-columns-desktop`]:
						gridItem.gridTemplateColumns[1],
				}
			: {}),
		...(Array.isArray(gridItem?.gridTemplateRows) &&
		gridItem?.gridTemplateRows.length > 1
			? {
					[`--grid-${gridElementLabel}-template-rows-desktop`]:
						gridItem.gridTemplateRows[1],
				}
			: {}),
	} as CSSProperties;
};

const generateGridStartEndStyles = (
	gridItem?: Partial<GridItemProps>,
	gridElementLabel: string = "item",
): CSSProperties => {
	return {
		...(gridItem?.gridColumn
			? {
					[`--grid-${gridElementLabel}-column`]: Array.isArray(
						gridItem.gridColumn,
					)
						? gridItem.gridColumn[0]
						: gridItem?.gridColumn,
				}
			: {}),
		...(gridItem?.gridRow
			? {
					[`--grid-${gridElementLabel}-row`]: Array.isArray(gridItem.gridRow)
						? gridItem.gridRow[0]
						: gridItem?.gridRow,
				}
			: {}),
		...(Array.isArray(gridItem?.gridColumn) && gridItem?.gridColumn.length > 1
			? {
					[`--grid-${gridElementLabel}-column-desktop`]: gridItem.gridColumn[1],
				}
			: {}),
		...(Array.isArray(gridItem?.gridRow) && gridItem?.gridRow.length > 1
			? {
					[`--grid-${gridElementLabel}-row-desktop`]: gridItem.gridRow[1],
				}
			: {}),
	} as CSSProperties;
};

const generateGridStyles = (
	gridItem?: Partial<
		GridItemProps &
			Pick<GridContainerProps, "gridTemplateColumns" | "gridTemplateRows">
	>,
	gridElementLabel: string = "item",
) => ({
	...generateGridTemplateStyles(gridItem, gridElementLabel),
	...generateGridStartEndStyles(gridItem, gridElementLabel),
});

const TitledListWithMedia = ({
	title,
	list,
	listType: ListComponent = "ul",
	media,
	grid,
	mediaMap,
	className,
}: TitledListWithMediaProps & {
	mediaMap?: ResponsiveImagesMap;
	className?: string;
}) => {
	const containerStyle = {
		...(grid?.gridTemplateColumns
			? { "--grid-template-columns": grid?.gridTemplateColumns }
			: {}),
		...(grid?.gridTemplateRows
			? { "--grid-template-rows": grid?.gridTemplateRows }
			: {}),
		...(grid?.paddingBlock ? { "--padding-block": grid?.paddingBlock } : {}),
		...(grid?.paddingInline ? { "--padding-inline": grid?.paddingInline } : {}),
	} as CSSProperties;

	const titleStyle = generateGridStartEndStyles(title, "title");

	const textListStyle = generateGridStartEndStyles(list, "text");

	const mediaItemStyle = generateGridStyles(media, "media-container");

	return (
		<section
			className={classNames("titled-list-with-media", className)}
			style={containerStyle}>
			<div className="grid">
				<div className="titled-list-with-media__title" style={titleStyle}>
					<MarkdownRenderer>{title.content}</MarkdownRenderer>
				</div>
				<div className="titled-list-with-media__text" style={textListStyle}>
					<ListComponent className="list">
						{list?.items.map((item, index) => {
							return (
								<li className="list__item" key={index}>
									<MarkdownRenderer>{item.content}</MarkdownRenderer>
								</li>
							);
						})}
					</ListComponent>
				</div>
				{media?.items && media.items.length > 0 && (
					<div
						className="titled-list-with-media__media"
						style={generateGridStartEndStyles(
							{
								gridColumn: media.gridColumn,
								gridRow: media.gridRow,
							},
							"media-container",
						)}>
						<div
							className="titled-list-with-media__media-grid"
							style={mediaItemStyle}>
							{media.items.map((mediaItem, index) => {
								if (!mediaMap) {
									return (
										<img
											key={index}
											src={mediaItem.img}
											alt={mediaItem.alt}
											className="titled-list-with-media__media__item"
											style={generateGridStartEndStyles(
												mediaItem,
												"media-item",
											)}
										/>
									);
								}

								const mediaValue =
									mediaMap[mediaItem.img as keyof typeof mediaMap];
								const isResponsiveImage =
									mediaValue &&
									typeof mediaValue === "object" &&
									"mobile" in mediaValue;

								return isResponsiveImage ? (
									<Picture
										key={index}
										mobileSrc={mediaValue.mobile}
										desktopSrc={mediaValue.desktop}
										alt={mediaItem.alt}
										className="titled-list-with-media__media__item"
										style={generateGridStartEndStyles(mediaItem, "media-item")}
										sizes={mediaItem.sizes}
									/>
								) : (
									<img
										key={index}
										src={
											typeof mediaValue === "string"
												? mediaValue
												: mediaItem.img
										}
										alt={mediaItem.alt}
										className="titled-list-with-media__media__item"
										style={generateGridStartEndStyles(mediaItem, "media-item")}
										sizes={mediaItem.sizes}
									/>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</section>
	);
};

export default TitledListWithMedia;
